// Plan constants
// Two plan tiers, matching the marketing site (pass.naya.ph/pricing):
// Starter ₱499 (single shop) and Pro ₱1,499 (multi-branch). The legacy
// 'business' tier was dropped in migration 0025.
export const PLANS = ['starter', 'pro'];
// Pass types (SLICE-PASS-TYPES). V1 ships 'loyalty'; 'membership' and
// 'event_ticket' land in their own slices. The discriminator lives on
// loyalty_programs.pass_type / customer_cards.pass_type, and the admin gates
// how many of each type a tenant may create via tenants.program_caps (NULL =
// inherit the plan tier default; per-type 0 = disabled).
export const PASS_TYPES = ['loyalty', 'membership', 'event_ticket'];
export const PLAN_PRICES_PHP_MONTHLY = {
    starter: 499,
    pro: 1499,
};
// Billing cycle (slice 3a — tenant_invoices + tenants.billing_cycle)
export const BILLING_CYCLES = ['monthly', 'annual'];
// Tenant lifecycle status (slice 3a — replaces 'pending_payment')
export const TENANT_STATUSES = ['pending_setup', 'active', 'suspended'];
// Audit log action enum — closed set per slice 3a §"Logged actions". The
// audit-writer accepts only these strings; impersonation-context mutations
// flow through 'impersonation_mutation' with the inner action carried in
// metadata.inner_action.
export const AUDIT_ACTIONS = [
    'admin_login',
    'admin_login_failed',
    'admin_logout',
    'tenant_created',
    'tenant_activated',
    'tenant_suspended',
    'tenant_unsuspended',
    'tenant_notes_updated',
    'tenant_plan_tier_changed',
    'tenant_plan_tier_reassigned',
    'tenant_plan_tier_applied',
    'tenant_logo_updated',
    'setup_link_regenerated',
    'owner_password_reset_link_sent',
    'owner_profile_updated',
    'branch_created',
    'branch_updated',
    'branch_archived',
    'branch_unarchived',
    'staff_created',
    'staff_updated',
    'staff_password_reset',
    'staff_deleted',
    'admin_created',
    'admin_deactivated',
    'admin_reactivated',
    'admin_password_changed',
    'impersonation_started',
    'impersonation_exited',
    'invoice_created',
    'invoice_marked_paid',
    'invoice_voided',
    'plan_tier_settings_updated',
    'impersonation_mutation',
    // Pass builder (slice 4a-3) — owner-side mutating program routes.
    'program_created',
    'program_updated',
    'program_activated',
    'program_stamp_icon_uploaded',
    'program_hero_uploaded',
    'program_hero_brightness_updated',
    'program_hero_removed',
    // Enrollment + stamping (slice 4a-4) — store-initiated customer enrollment
    // (via staff QR handoff) and stamp redemption events.
    'customer_enrolled_by_store',
    'stamp_issued',
    'stamp_redeemed',
    'enrollment_token_minted',
    // Broadcasts (slice 5) — owner-triggered wallet push announcements.
    'broadcast_created',
    'broadcast_cancelled',
    'broadcast_removed',
    // Superadmin analytics — manual refresh of the daily rollup.
    'analytics_refreshed',
    // Membership pass (SLICE-MEMBERSHIP-PASS) — per-card tier change, term
    // renewal, and the verify-scan check-in.
    'membership_updated',
    'membership_renewed',
    'membership_checkin',
    // Event ticket pass (SLICE-EVENT-TICKET-PASS) — owner issues tickets, voids a
    // ticket, and the gate-scan check-in (active → used).
    'ticket_issued',
    'ticket_voided',
    'ticket_checked_in',
    // Pre-existing gap fix (2026-09-05) — these two routes have declared these
    // auditAction strings since 4a-4/4b but the keys were never added here.
    'card_reset',
    'redemption',
    // Customer lifecycle (2026-09-05) — owner-side stamp correction and
    // revoke/erase. See CustomersPage's danger zone + issueStamp's guard.
    'stamp_adjusted',
    'customer_revoked',
    'customer_unrevoked',
    'customer_data_erased',
];
// Invoice status (tenant_invoices.status). 'unpaid' replaced 'pending' in
// slice 3c for terminology consistency; the state machine is unchanged.
export const INVOICE_STATUSES = ['unpaid', 'paid', 'overdue', 'void'];
// Manual payment methods accepted in V1 (no gateway).
export const PAYMENT_METHODS = ['gcash', 'bank'];
// Roles
export const ROLES = ['superadmin', 'owner', 'staff'];
// Loyalty program limits
export const LOYALTY_MIN_STAMPS = 5;
export const LOYALTY_MAX_STAMPS = 15;
export const LOYALTY_DEFAULT_STAMPS = 10;
// Membership pass limits (SLICE-MEMBERSHIP-PASS). Benefits render on the back
// of the pass — 8 lines max so Apple's back fields and Google's textModule
// stay readable. Tier + label caps keep Apple's fixed-width front fields from
// silently truncating.
export const MEMBERSHIP_MAX_BENEFITS = 8;
export const MEMBERSHIP_BENEFIT_MAX_LENGTH = 120;
export const MEMBERSHIP_TIER_MAX_LENGTH = 24;
export const MEMBERSHIP_NUMBER_LABEL_MAX_LENGTH = 24;
export const DEFAULT_MEMBER_NUMBER_LABEL = 'Member No.';
// SLICE-MEMBERSHIP-PASS — when an owner turns tiers ON they define a list of
// selectable tier options (Gold / Silver / Bronze). One is chosen per member at
// enrollment. Capped so the enrollment picker + builder list stay manageable.
export const MEMBERSHIP_MAX_TIERS = 8;
// Event ticket pass limits (SLICE-EVENT-TICKET-PASS). Tiers (GA / VIP / Early
// Bird) each carry a name + optional price + optional perks. Caps keep the
// builder list, the public claim picker, and Apple's fixed-width front fields
// readable. Inclusions render on the back of the pass.
export const EVENT_NAME_MAX_LENGTH = 80;
export const EVENT_VENUE_NAME_MAX_LENGTH = 80;
export const EVENT_VENUE_ADDRESS_MAX_LENGTH = 160;
export const EVENT_MAX_TIERS = 8;
export const EVENT_TIER_NAME_MAX_LENGTH = 24;
export const EVENT_MAX_INCLUSIONS = 8;
export const EVENT_INCLUSION_MAX_LENGTH = 120;
export const EVENT_SEAT_FIELD_MAX_LENGTH = 24;
export const EVENT_HOLDER_NAME_MAX_LENGTH = 60;
// Default tier seeded into a fresh event draft so every ticket has a tier even
// before the owner customizes the list.
export const DEFAULT_EVENT_TIER_NAME = 'General Admission';
// V1 scale guard on a single owner batch-issue. Concert-scale (thousands) needs
// an async worker — a future slice. Above this the issue route 422s.
export const EVENT_MAX_BATCH_ISSUE = 200;
// ----- Pass builder (slice 4a-3) -----
// Program lifecycle. 'draft' → owner is building it in the wizard;
// 'active' → enrollment-eligible, structural fields locked; 'archived' →
// superseded (admin/DB only, no UI).
export const PROGRAM_STATUSES = ['draft', 'active', 'archived'];
// What happens when a card reaches the grand prize.
export const COMPLETION_BEHAVIORS = ['reset', 'stay_completed'];
// Pass-back link types the owner can add (max 5 links per program).
export const BACK_LINK_TYPES = [
    'url',
    'phone',
    'email',
    'address',
    'facebook',
    'instagram',
    'tiktok',
];
export const MAX_BACK_LINKS = 5;
// Optional onboarding fields the owner can toggle on. first_name + email are
// always-on and not in this list (immutable — see OnboardingFieldsSchema).
// `birthday` collects month + day together (MM-DD) — matches the existing
// `customers.birth_month_day` column. Earlier 4a-3 drafts split this into
// birth_day + birth_month; migration 0010 merges those into a single
// `birthday` key in onboarding_fields jsonb.
export const OPTIONAL_ONBOARDING_FIELDS = [
    'last_name',
    'birthday',
    'phone',
];
// Every onboarding field key, immutable + optional. The owner picks a subset
// of the *enabled* ones to display on the pass front (pass_design.front_fields).
// ORDER IS LOAD-BEARING: this is the canonical render order for the enrollment
// form (public + owner/staff hand-off) and the pass detail row. It reads
// first → last → email → birthday → phone so the name fields stay together
// (email between first/last name was the prior, jarring order). Applies to
// BOTH loyalty and membership — they share this constant.
export const ALL_ONBOARDING_FIELDS = [
    'first_name',
    'last_name',
    'email',
    'birthday',
    'phone',
];
// Human labels for onboarding field keys — used by the builder UI and the
// pass-front detail row.
export const ONBOARDING_FIELD_LABELS = {
    first_name: 'First name',
    email: 'Email',
    last_name: 'Last name',
    birthday: 'Birthday',
    phone: 'Phone',
};
// The pass front shows at most this many detail fields. Up to 4 — the detail
// row wraps to a second line on the pass when the owner picks more than 2.
// first_name is the default selection on a fresh draft.
export const MAX_FRONT_FIELDS = 4;
// Slice 4b — empty by default. first_name was historically the default but
// it's a duplicate of Google's accountName chrome ("Member · Juan" on the
// back of the pass), so showing it again in the front detail row is
// redundant. Owner can pick last_name / birthday / phone if they want
// anything in the detail row.
export const DEFAULT_FRONT_FIELDS = [];
// Built-in stamp icons. Each id maps to a bundled SVG in
// apps/web/src/assets/stamp-icons/. Stored in pass_design as
// `builtin:<id>:filled` / `builtin:<id>:empty`; a custom upload stores an
// `https://` (or `/dev-uploads/...`) URL instead.
export const BUILTIN_STAMP_ICONS = [
    'star',
    'circle',
    'square',
    'heart',
    'coffee',
    'paw',
];
export const DEFAULT_STAMP_ICON = 'star';
// Stamp icon upload constraints — custom icons are 200×200 PNG.
export const STAMP_ICON_DIMENSION = 200;
export const STAMP_ICON_MAX_BYTES = 512 * 1024; // 512KB — icons are tiny
export const STAMP_ICON_ALLOWED_MIME_TYPES = ['image/png'];
// Owner-supplied pass-back terms cap. Long enough for expiry / fine print,
// short enough to render on a Google Wallet pass back without truncation.
export const OWNER_TERMS_MAX_LENGTH = 500;
// Naya terracotta — the builder's default primary color.
export const NAYA_TERRACOTTA = '#A84F38';
// Broadcast rate limit
export const BROADCAST_SOFT_LIMIT_PER_WEEK = 1;
// Setup link expiry. Onboarding invites get a long window (sent to an owner who
// may take days to act). A PASSWORD RESET link is for an existing privileged
// account and is a full-takeover credential — it gets a short window.
export const SETUP_TOKEN_TTL_DAYS = 14;
export const PASSWORD_RESET_TOKEN_TTL_HOURS = 2;
// Wallet providers
export const WALLET_PROVIDERS = ['google', 'apple'];
// Tenant slug rules
export const SLUG_MIN_LENGTH = 3;
export const SLUG_MAX_LENGTH = 30;
export const SLUG_FORMAT_REGEX = /^[a-z][a-z0-9-]{2,29}$/;
// Reserved slugs: rejected by tenant creation/edit. Mirrors the same list the
// migration backfill avoids assigning.
export const RESERVED_SLUGS = [
    'api',
    'admin',
    't',
    'auth',
    'login',
    'static',
    'assets',
    'c',
    'save',
    'www',
    'naya',
    'pass',
    'app',
    'mail',
    'docs',
    'help',
    'support',
];
// Save-token TTL (milliseconds)
export const SAVE_TOKEN_TTL_MS = 10 * 60 * 1000;
// Public-facing base URL for save links. Read by the API at runtime via env;
// the constant here is the production default and the fallback for tests.
export const DEFAULT_PASS_BASE_URL = 'https://app.naya.ph';
// ----- Broadcast audience segmentation -----
//
// An owner targets a broadcast by an `audience` descriptor (see
// `BroadcastAudienceSchema` in schemas). Two legacy "kinds" — `everyone` and
// `birthday_this_month` — are unchanged from slice 5. New audiences are either
// a curated `preset` (a named, ready-made segment) or a `filter` (a small set
// of AND-combined rules the owner composes).
// The dimensions a filter rule can target. Each maps to a customer/card fact.
export const SEGMENT_DIMENSIONS = [
    'stamps', // stamps on the current card
    'milestone_gap', // stamps remaining to the next unclaimed milestone
    'last_stamp', // recency of the most recent stamp
    'enrolled', // how long ago the card was started
    'reward_state', // has-unclaimed / no-stamps / completed-before
    'birthday', // birthday falls in the current month
];
// At most this many rules in one `filter` audience (AND-combined).
export const MAX_SEGMENT_RULES = 3;
// Curated preset keys. Each resolves server-side to a canonical rule set
// (see `BROADCAST_PRESETS`). The owner picks a preset by key; the resolver is
// the single source of truth for what each one means.
export const BROADCAST_PRESET_KEYS = [
    'almost_at_reward',
    'one_stamp_away',
    'new_no_stamps',
    'reward_waiting',
    'lapsed_60d',
    'active_30d',
    'joined_this_week',
    'birthday_month',
];
// Owner-facing label + description for each preset. Used by the composer's
// preset chips. The `rules` that back each preset live in `BROADCAST_PRESETS`
// in schemas (they need the Rule type).
export const BROADCAST_PRESET_META = {
    almost_at_reward: {
        label: 'Almost at a reward',
        description: 'Customers within 2 stamps of their next reward.',
    },
    one_stamp_away: {
        label: 'One stamp away',
        description: 'Customers a single stamp from their next reward.',
    },
    new_no_stamps: {
        label: 'New, no stamps yet',
        description: 'Have a pass but have not earned a stamp.',
    },
    reward_waiting: {
        label: 'Reward waiting to be claimed',
        description: 'Earned a reward they have not redeemed yet.',
    },
    lapsed_60d: {
        label: 'Lapsed customers',
        description: 'No stamp earned in the last 60 days.',
    },
    active_30d: {
        label: 'Recently active',
        description: 'Earned a stamp in the last 30 days.',
    },
    joined_this_week: {
        label: 'Just joined',
        description: 'Signed up in the last 7 days.',
    },
    birthday_month: {
        label: 'Birthday this month',
        description: 'Birthday falls in the current month.',
    },
};
//# sourceMappingURL=index.js.map